import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AdmissionDataMock } from '@type/admission.type'
import { ConsentFormDataMock } from '@type/consent_form.type'
import { AppointmentAttributes } from '@type/dashboard.type'
import { DepartmentsMock } from '@type/department.types'
import { MedDataMock } from '@type/history.type'
import { PatientImagingResult } from '@type/imaging_results.type'
import { LabDataMock } from '@type/lab.type'
import { LabResult } from '@type/lab_results.type'
import { LocationMock } from '@type/location.type'
import { MedicationDataMock } from '@type/medication.type'
import { ModalProps } from '@type/modal.types'
import { OrderDataMock } from '@type/order.type'
import { PatAttributes, PatientSAttrProps } from '@type/patient.types'
import { ProceedAttributesMock } from '@type/proceed.type'
import { Attributes } from '@type/role.type'
import { SchedulEventMock } from '@type/schedule.type'
import { ShiftAttributes } from '@type/shift.type'
import { VitalDataMock } from '@type/vital.type'

const initialState: ModalProps = {
  admission: {
    add: false,
    delete: {
      defaultData: undefined,
      id: '',
      show: false
    },
    edit: {
      defaultData: undefined,
      id: '',
      show: false
    }
  },
  dashboards: {
    doctor: {
      details: {
        defaultData: undefined,
        id: '',
        show: false
      }
    }
  },
  demography: {
    contact: {
      defaultData: undefined,
      id: '',
      show: false
    },
    employment: {
      defaultData: undefined,
      id: '',
      show: false
    },
    guardian: {
      defaultData: undefined,
      id: '',
      show: false
    },
    misc: {
      defaultData: undefined,
      id: '',
      show: false
    },
    personal: {
      defaultData: undefined,
      id: '',
      show: false
    }
  },
  department: {
    add: false,
    delete: {
      id: '',
      show: false
    },
    edit: {
      defaultData: undefined,
      id: '',
      show: false
    },
    success: false
  },
  history: {
    medical: {
      add: false,
      delete: {
        id: '',
        show: false
      },
      details: {
        defaultData: undefined,
        id: '',
        show: false
      },
      edit: {
        defaultData: undefined,
        id: '',
        show: false
      }
    }
  },
  imaging: {
    add: false,
    delete: {
      id: '',
      show: false
    },
    detail: {
      defaultData: undefined,
      id: '',
      show: false
    },
    edit: {
      defaultData: undefined,
      id: '',
      show: false
    },
    img: {
      id: '',
      show: false
      // defaultData: undefined
    }
  },
  imagingResultDetails: {
    defaultData: undefined,
    id: '',
    show: false
  },
  inventory: {
    equipment: {
      add: false,
      delete: {
        id: '',
        show: false
      },
      details: {
        defaultData: undefined,
        id: '',
        show: false
      },
      edit: {
        defaultData: undefined,
        id: '',
        show: false
      },
      success: false
    }
  },
  lab: {
    delete: {
      id: '',
      show: false
    },
    edit: {
      defaultData: undefined,
      id: '',
      show: false
    }
  },
  labResultDetails: {
    defaultData: undefined,
    id: '',
    show: false
  },
  location: {
    add: false,
    delete: {
      id: '',
      show: false
    },
    edit: {
      defaultData: undefined,
      id: '',
      show: false
    },
    success: false
  },
  medication: {
    add: false,
    custom_frequency: {
      add: false,
      edit: {
        defaultData: undefined,
        id: '',
        show: false
      }
    },
    delete: {
      id: '',
      show: false
    },
    detail: {
      defaultData: undefined,
      id: '',
      show: false
    },
    edit: {
      defaultData: undefined,
      id: '',
      show: false
    }
  },
  patient: {
    add: false,
    delete: {
      id: '',
      show: false
    },
    detail: {
      id: '',
      show: false
    },
    edit: {
      defaultData: undefined,
      id: '',
      show: false
    }
  },
  permission: {
    add: false,
    delete: {
      id: '',
      show: false
    },
    edit: {
      defaultData: undefined,
      id: '',
      show: false
    },
    success: false
  },
  previewImagingResult: {
    defaultData: undefined,
    id: '',
    show: false
  },
  problems: {
    add: false,
    edit: {
      defaultData: undefined,
      id: '',
      show: false
    }
  },
  procedure: {
    add: false,
    admin: {
      detail: {
        defaultData: undefined,
        id: '',
        show: false
      },
      edit: {
        defaultData: undefined,
        id: '',
        show: false
      }
    },
    delete: {
      id: '',
      show: false
    },
    doctor: {
      detail: {
        defaultData: undefined,
        id: '',
        show: false
      },
      edit: {
        defaultData: undefined,
        id: '',
        show: false
      }
    },
    notes: {
      defaultData: undefined,
      id: '',
      show: false
    }
  },
  schedule: {
    add: false,
    delete: {
      defaultData: undefined,
      id: '',
      show: false
    },
    detail: {
      defaultData: undefined,
      id: '',
      show: false
    },
    edit: {
      defaultData: undefined,
      id: '',
      show: false
    }
  },
  shift: {
    add: false,
    delete: {
      id: '',
      show: false
    },
    edit: {
      defaultData: undefined,
      id: '',
      show: false
    },
    success: false
  },
  staff: {
    delete: {
      id: '',
      show: false
    },
    detail: {
      id: '',
      show: false
    },
    filter: false,
    search: false
  },
  submitLabResult: { id: '', show: false },
  submitLabSample: { id: '', show: false },
  supplier: {
    add: false,
    delete: {
      id: '',
      show: false
    },
    detail: {
      id: '',
      show: false
    },
    edit: {
      defaultData: undefined,
      id: '',
      show: false
    },
    success: false
  },
  viewImagingResultFile: {
    defaultData: undefined,
    id: '',
    show: false
  },
  vitals: {
    compare: {
      add: {
        defaultData: undefined,
        id: '',
        show: false
      },
      table: {
        defaultData: undefined,
        id: '',
        show: false
      }
    },
    delete: {
      id: '',
      show: false
    },
    detail: {
      defaultData: undefined,
      id: '',
      show: false
    },
    edit: {
      defaultData: undefined,
      id: '',
      show: false
    },
    showGraph: false
  }
}

const ModalSlicer = createSlice({
  initialState,
  name: 'modal',
  reducers: {
    hideAddAdmissionOrder: state => {
      state.admission.add = false
    },
    hideAddCompareVitals: state => {
      state.vitals.compare.add.show = false
    },
    hideAddDepartment: state => {
      state.department.add = false
    },
    hideAddEmployment: state => {
      state.demography.employment.show = false
      state.demography.employment.id = ''
    },
    hideAddEquipment: state => {
      state.inventory.equipment.add = false
    },
    hideAddGuardian: state => {
      state.demography.guardian.show = false
      state.demography.guardian.id = ''
    },
    hideAddImaging: state => {
      state.imaging.add = false
    },
    hideAddLocation: state => {
      state.location.add = false
    },
    hideAddMedicalHistory: state => {
      state.history.medical.add = false
    },
    hideAddMedications: state => {
      state.medication.add = false
    },
    hideAddMisc: state => {
      state.demography.misc.show = false
      state.demography.misc.id = ''
    },
    hideAddPatient: state => {
      state.patient.add = false
    },
    hideAddPersonal: state => {
      state.demography.personal.show = false
      state.demography.personal.id = ''
    },
    hideAddProblems: state => {
      state.problems.add = false
    },
    hideAddProcedure: state => {
      state.procedure.add = false
    },
    hideAddProcedureNotes: state => {
      state.procedure.notes.show = false
    },
    hideAddRoles: state => {
      state.permission.add = false
    },
    hideAddSchedule: state => {
      state.schedule.add = false
    },
    hideAddShift: state => {
      state.shift.add = false
    },
    hideAddSupplier: state => {
      state.supplier.add = false
    },
    hideCancelProcedure: state => {
      state.procedure.delete.show = false
      state.procedure.delete.id = ''
    },
    hideCompareVitalsTable: state => {
      state.vitals.compare.table.show = false
      state.vitals.compare.table.id = ''
    },
    hideContact: state => {
      state.demography.contact.show = false
      state.demography.contact.id = ''
    },
    hideDeleteAdmissionOrder: state => {
      state.admission.delete.show = false
      state.admission.delete.id = ''
      state.admission.delete.defaultData = undefined
    },
    hideDeleteDepartment: state => {
      state.department.delete.show = false
      state.department.delete.id = ''
    },
    hideDeleteImaging: state => {
      state.imaging.delete.show = false
      state.imaging.delete.id = ''
    },
    hideDeleteLab: state => {
      state.lab.delete.show = false
      state.lab.delete.id = ''
    },
    hideDeleteLocation: state => {
      state.location.delete.show = false
      state.location.delete.id = ''
    },
    hideDeleteMedicalHistory: state => {
      state.history.medical.delete.show = false
      state.history.medical.delete.id = ''
    },
    hideDeleteMedications: state => {
      state.medication.delete.show = false
      state.medication.delete.id = ''
    },
    hideDeletePatient: state => {
      state.patient.delete.show = false
      state.patient.delete.id = ''
    },
    hideDeleteProcedure: state => {
      state.procedure.delete.show = false
      state.procedure.delete.id = ''
    },
    hideDeleteRoles: state => {
      state.permission.delete.show = false
      state.permission.delete.id = ''
    },
    hideDeleteSchedule: state => {
      state.schedule.delete.show = false
      state.schedule.delete.id = ''
    },
    hideDeleteShift: state => {
      state.shift.delete.show = false
      state.shift.delete.id = ''
    },
    hideDeleteStaff: state => {
      state.staff.delete.show = false
      state.staff.delete.id = ''
    },
    hideDeleteSupplier: state => {
      state.supplier.delete.show = false
      state.supplier.delete.id = ''
    },
    hideDeleteVitals: state => {
      state.vitals.delete.show = false
      state.vitals.delete.id = ''
    },
    hideDepSuccess: state => {
      state.department.success = false
    },
    hideDoctorAppointment: state => {
      state.dashboards.doctor.details.show = false
      state.dashboards.doctor.details.id = ''
    },
    hideEditAdmissionOrder: state => {
      state.admission.edit.show = false
      state.admission.edit.id = ''
      state.admission.edit.defaultData = undefined
    },
    hideEditDepartment: state => {
      state.department.edit.show = false
      state.department.edit.id = ''
    },
    hideEditImaging: state => {
      state.imaging.edit.show = false
      state.imaging.edit.id = ''
    },
    hideEditLab: state => {
      state.lab.edit.show = false
      state.lab.edit.id = ''
    },
    hideEditLocation: state => {
      state.location.edit.show = false
      state.location.edit.id = ''
    },
    hideEditMedicalHistory: state => {
      state.history.medical.edit.show = false
      state.history.medical.edit.id = ''
    },
    hideEditMedicationFrequency: state => {
      state.medication.custom_frequency.edit.show = false
      state.medication.custom_frequency.edit.id = ''
    },
    hideEditMedications: state => {
      state.medication.edit.show = false
      state.medication.edit.id = ''
    },
    hideEditPermission: state => {
      state.permission.edit.show = false
      state.permission.edit.id = ''
    },
    hideEditProblems: state => {
      state.problems.edit.show = false
      state.problems.edit.id = ''
    },
    hideEditProcedure: state => {
      state.procedure.admin.edit.show = false
      state.procedure.admin.edit.id = ''
    },
    hideEditProcedureOrder: state => {
      state.procedure.doctor.edit.show = false
      state.procedure.doctor.edit.id = ''
    },
    hideEditSchedule: state => {
      state.schedule.edit.show = false
      state.schedule.edit.id = ''
    },
    hideEditShift: state => {
      state.shift.edit.show = false
      state.shift.edit.id = ''
    },
    hideEditSupplier: state => {
      state.supplier.edit.show = false
      state.supplier.edit.id = ''
    },
    hideEditVitals: state => {
      state.vitals.edit.show = false
      state.vitals.edit.id = ''
    },
    hideEquipmentDetails: state => {
      state.inventory.equipment.details.show = false
      state.inventory.equipment.details.id = ''
    },
    hideFilterStaff: state => {
      state.staff.filter = false
    },
    hideImagingDetails: state => {
      state.imaging.detail.show = false
      state.imaging.detail.id = ''
    },
    hideImagingResultDetails: state => {
      state.imagingResultDetails.show = false
      state.imagingResultDetails.id = ''
      state.imagingResultDetails.defaultData = undefined
    },
    hideImg: state => {
      state.imaging.img.show = false
      state.imaging.img.id = ''
    },
    hideLabResultDetails: state => {
      state.labResultDetails.show = false
      state.labResultDetails.id = ''
      state.labResultDetails.defaultData = undefined
    },
    hideLocationSuccess: state => {
      state.location.success = false
    },
    hideMedicalHistoryDetails: state => {
      state.history.medical.details.show = false
      state.history.medical.details.id = ''
    },
    hideMedicationDetails: state => {
      state.medication.detail.show = false
      state.medication.detail.id = ''
    },
    hideMedicationFrequency: state => {
      state.medication.custom_frequency.add = false
    },
    hidePatientDetail: state => {
      state.patient.detail.show = false
      state.patient.detail.id = ''
    },
    hidePreviewImagingResult: state => {
      state.previewImagingResult = {
        defaultData: undefined,
        id: '',
        show: false
      }
    },
    hideProcedureDetails: state => {
      state.procedure.admin.detail.show = false
      state.procedure.admin.detail.id = ''
    },
    hideProcedureOrderDetails: state => {
      state.procedure.doctor.detail.show = false
      state.procedure.doctor.detail.id = ''
    },
    hideRoleSuccess: state => {
      state.permission.success = false
    },
    hideScheduleDetails: state => {
      state.schedule.detail.show = false
    },
    hideSearch: state => {
      state.staff.search = false
    },
    hideShiftSuccess: state => {
      state.shift.success = false
    },
    hideStaffDetail: state => {
      state.staff.detail.show = false
      state.staff.detail.id = ''
    },
    hideSubmitLabResult: state => {
      state.submitLabResult.show = false
      state.submitLabResult.id = ''
    },
    hideSubmitLabSample: state => {
      state.submitLabSample.show = false
      state.submitLabSample.id = ''
    },
    hideSupplierDetail: state => {
      state.supplier.detail.show = false
      state.supplier.detail.id = ''
    },
    hideSupplierSuccess: state => {
      state.supplier.success = false
    },
    hideUpdatePatient: state => {
      state.patient.edit.show = false
      state.patient.edit.id = ''
      state.patient.edit.defaultData = undefined
    },
    hideViewImagingResultFile: state => {
      state.viewImagingResultFile.show = false
      state.viewImagingResultFile.id = ''
      state.viewImagingResultFile.defaultData = undefined
    },
    hideVitalDetails: state => {
      state.vitals.detail.show = false
      state.vitals.detail.id = ''
    },
    hideVitalsGraph: state => {
      state.vitals.showGraph = false
    },
    showAddAdmissionOrder: state => {
      state.admission.add = true
    },
    showAddCompareVitals: state => {
      state.vitals.compare.add.show = true
    },
    showAddDepartment: state => {
      state.department.add = true
    },
    showAddEmployment: (
      state,
      { payload }: PayloadAction<{ id: string; defaultData: Record<string, string> | undefined }>
    ) => {
      state.demography.employment.show = true
      state.demography.employment.id = payload.id
      state.demography.employment.defaultData = payload.defaultData
    },
    showAddEquipment: state => {
      state.inventory.equipment.add = true
    },
    showAddGuardian: (
      state,
      { payload }: PayloadAction<{ id: string; defaultData: Record<string, string> | undefined }>
    ) => {
      state.demography.guardian.show = true
      state.demography.guardian.id = payload.id
      state.demography.guardian.defaultData = payload.defaultData
    },
    showAddImaging: state => {
      state.imaging.add = true
    },
    showAddLocation: state => {
      state.location.add = true
    },
    showAddMedicalHistory: state => {
      state.history.medical.add = true
    },
    showAddMedications: state => {
      state.medication.add = true
    },
    showAddMisc: (
      state,
      { payload }: PayloadAction<{ id: string; defaultData: Record<string, string> | undefined }>
    ) => {
      state.demography.misc.show = true
      state.demography.misc.id = payload.id
      state.demography.misc.defaultData = payload.defaultData
    },
    showAddPatient: state => {
      state.patient.add = true
    },
    showAddPersonal: (
      state,
      { payload }: PayloadAction<{ id: string; defaultData: PatientSAttrProps | undefined }>
    ) => {
      state.demography.personal.show = true
      state.demography.personal.id = payload.id
      state.demography.personal.defaultData = payload.defaultData
    },
    showAddProblems: state => {
      state.problems.add = true
    },
    showAddProcedure: state => {
      state.procedure.add = true
    },
    showAddProcedureNotes: (state, { payload }: PayloadAction<{ id: string; defaultData?: ConsentFormDataMock }>) => {
      state.procedure.notes.show = true
      state.procedure.notes.id = payload.id
      state.procedure.notes.defaultData = payload.defaultData
    },
    showAddRoles: state => {
      state.permission.add = true
    },
    showAddSchedule: state => {
      state.schedule.add = true
    },
    showAddShift: state => {
      state.shift.add = true
    },
    showAddSupplier: state => {
      state.supplier.add = true
    },
    showCancelProcedure: (state, { payload }: PayloadAction<{ id: string; defaultData?: ConsentFormDataMock }>) => {
      state.procedure.delete.show = true
      state.procedure.delete.id = payload.id
      state.procedure.notes.defaultData = payload.defaultData
    },
    showCompareVitalsTable: (
      state,
      { payload }: PayloadAction<{ id: string; defaultData: VitalDataMock | undefined }>
    ) => {
      state.vitals.compare.table.show = true
      state.vitals.compare.table.id = payload.id
      state.vitals.compare.table.defaultData = payload.defaultData
    },
    showContact: (state, { payload }: PayloadAction<{ id: string; defaultData: undefined }>) => {
      state.demography.contact.show = true
      state.demography.contact.id = payload.id
      state.demography.contact.defaultData = payload.defaultData
    },
    showDeleteAdmissionOrder: (state, { payload }: PayloadAction<{ id: string }>) => {
      state.admission.delete.show = true
      state.admission.delete.id = payload.id
    },
    showDeleteDepartment: (state, { payload }: PayloadAction<{ id: string }>) => {
      state.department.delete.show = true
      state.department.delete.id = payload.id
    },
    showDeleteImaging: (state, { payload }: PayloadAction<{ id: string }>) => {
      state.imaging.delete.show = true
      state.imaging.delete.id = payload.id
    },
    showDeleteLab: (state, { payload }: PayloadAction<{ id: string }>) => {
      state.lab.delete.show = true
      state.lab.delete.id = payload.id
    },
    showDeleteLocation: (state, { payload }: PayloadAction<{ id: string }>) => {
      state.location.delete.show = true
      state.location.delete.id = payload.id
    },
    showDeleteMedicalHistory: (
      state,
      { payload }: PayloadAction<{ id: string; defaultData?: MedDataMock | undefined }>
    ) => {
      state.history.medical.delete.show = true
      state.history.medical.delete.id = payload.id
    },
    showDeleteMedications: (state, { payload }: PayloadAction<{ id: string }>) => {
      state.medication.delete.show = true
      state.medication.delete.id = payload.id
    },
    showDeletePatient: (state, { payload }: PayloadAction<{ id: string }>) => {
      state.patient.delete.show = true
      state.patient.delete.id = payload.id
    },
    showDeleteProcedure: (state, { payload }: PayloadAction<{ id: string; defaultData: undefined }>) => {
      state.procedure.delete.show = true
      state.procedure.delete.id = payload.id
    },
    showDeleteRoles: (state, { payload }: PayloadAction<{ id: string }>) => {
      state.permission.delete.show = true
      state.permission.delete.id = payload.id
    },
    showDeleteSchedule: (state, { payload }: PayloadAction<{ id: string }>) => {
      state.schedule.delete.show = true
      state.schedule.delete.id = payload.id
    },
    showDeleteShift: (state, { payload }: PayloadAction<{ id: string }>) => {
      state.shift.delete.show = true
      state.shift.delete.id = payload.id
    },
    showDeleteStaff: (state, { payload }: PayloadAction<{ id: string }>) => {
      state.staff.delete.show = true
      state.staff.delete.id = payload.id
    },
    showDeleteSupplier: (state, { payload }: PayloadAction<{ id: string }>) => {
      state.supplier.delete.show = true
      state.supplier.delete.id = payload.id
    },
    showDeleteVitals: (state, { payload }: PayloadAction<{ id: string }>) => {
      state.vitals.delete.show = true
      state.vitals.delete.id = payload.id
    },

    showDepSuccess: state => {
      state.department.success = true
    },
    showDoctorAppointment: (
      state,
      { payload }: PayloadAction<{ id: string; defaultData: { event: AppointmentAttributes; patient: PatAttributes } }>
    ) => {
      state.dashboards.doctor.details.show = true
      state.dashboards.doctor.details.id = payload.id
      state.dashboards.doctor.details.defaultData = payload.defaultData
    },
    showEditAdmissionOrder: (state, { payload }: PayloadAction<{ id: string; defaultData: AdmissionDataMock }>) => {
      state.admission.edit.show = true
      state.admission.edit.id = payload.id
      state.admission.edit.defaultData = payload.defaultData
    },
    showEditDepartment: (
      state,
      { payload }: PayloadAction<{ id: string; defaultData: DepartmentsMock | undefined }>
    ) => {
      state.department.edit.show = true
      state.department.edit.id = payload.id
      state.department.edit.defaultData = payload.defaultData
    },
    showEditImaging: (state, { payload }: PayloadAction<{ id: string; defaultData: OrderDataMock | undefined }>) => {
      state.imaging.edit.show = true
      state.imaging.edit.id = payload.id
      state.imaging.edit.defaultData = payload.defaultData
    },
    showEditLab: (state, { payload }: PayloadAction<{ id: string; defaultData: LabDataMock }>) => {
      state.lab.edit.show = true
      state.lab.edit.id = payload.id
      state.lab.edit.defaultData = payload.defaultData
    },
    showEditLocation: (state, { payload }: PayloadAction<{ id: string; defaultData: LocationMock | undefined }>) => {
      state.location.edit.show = true
      state.location.edit.id = payload.id
      state.location.edit.defaultData = payload.defaultData
    },
    showEditMedicalHistory: (
      state,
      { payload }: PayloadAction<{ id: string; defaultData: MedDataMock | undefined }>
    ) => {
      state.history.medical.edit.show = true
      state.history.medical.edit.id = payload.id
      state.history.medical.edit.defaultData = payload.defaultData
    },
    showEditMedicationFrequency: (
      state,
      {
        payload
      }: PayloadAction<{ id: string; defaultData: MedicationDataMock['attributes']['custom_frequency'] | undefined }>
    ) => {
      state.medication.custom_frequency.edit.show = true
      state.medication.custom_frequency.edit.id = payload.id
      state.medication.custom_frequency.edit.defaultData = payload.defaultData
    },
    showEditMedications: (
      state,
      { payload }: PayloadAction<{ id: string; defaultData: MedicationDataMock | undefined }>
    ) => {
      state.medication.edit.show = true
      state.medication.edit.id = payload.id
      state.medication.edit.defaultData = payload.defaultData
    },
    showEditPermission: (state, { payload }: PayloadAction<{ id: string; defaultData: Attributes | undefined }>) => {
      state.permission.edit.show = true
      state.permission.edit.id = payload.id
      state.permission.edit.defaultData = payload.defaultData
    },
    showEditProblems: (state, { payload }: PayloadAction<{ id: string; defaultData: MedDataMock | undefined }>) => {
      state.problems.edit.show = true
      state.problems.edit.id = payload.id
      state.problems.edit.defaultData = payload.defaultData
    },
    showEditProcedure: (state, { payload }: PayloadAction<{ id: string; defaultData: ProceedAttributesMock }>) => {
      state.procedure.admin.edit.show = true
      state.procedure.admin.edit.id = payload.id
      state.procedure.admin.edit.defaultData = payload.defaultData
    },
    showEditProcedureOrder: (state, { payload }: PayloadAction<{ id: string; defaultData: any }>) => {
      state.procedure.doctor.edit.show = true
      state.procedure.doctor.edit.id = payload.id
      state.procedure.doctor.edit.defaultData = payload.defaultData
    },
    showEditSchedule: (state, { payload }: PayloadAction<{ id: string; defaultData?: undefined }>) => {
      state.schedule.edit.show = true
      state.schedule.edit.id = payload.id
      state.schedule.edit.defaultData = payload.defaultData
    },
    showEditShift: (state, { payload }: PayloadAction<{ id: string; defaultData: ShiftAttributes | undefined }>) => {
      state.shift.edit.show = true
      state.shift.edit.id = payload.id
      state.shift.edit.defaultData = payload.defaultData
    },
    showEditSupplier: (state, { payload }: PayloadAction<{ id: string; defaultData: undefined }>) => {
      state.supplier.edit.show = true
      state.supplier.edit.id = payload.id
      state.supplier.edit.defaultData = payload.defaultData
    },
    showEditVitals: (state, { payload }: PayloadAction<{ id: string; defaultData: VitalDataMock | undefined }>) => {
      state.vitals.edit.show = true
      state.vitals.edit.id = payload.id
      state.vitals.edit.defaultData = payload.defaultData
    },
    showEquipmentDetails: (state, { payload }: PayloadAction<{ id: string; defaultData: undefined }>) => {
      state.inventory.equipment.details.show = true
      state.inventory.equipment.details.id = payload.id
      state.inventory.equipment.details.defaultData = payload.defaultData
    },
    showFilterStaff: state => {
      state.staff.filter = true
    },
    showImagingDetails: (state, { payload }: PayloadAction<{ id: string; defaultData: OrderDataMock | undefined }>) => {
      state.imaging.detail.show = true
      state.imaging.detail.id = payload.id
      state.imaging.detail.defaultData = payload.defaultData
    },
    showImagingResultDetails: (
      state,
      { payload }: PayloadAction<{ id: string; defaultData: PatientImagingResult['data'][number] }>
    ) => {
      state.imagingResultDetails.show = true
      state.imagingResultDetails.id = payload.id
      state.imagingResultDetails.defaultData = payload.defaultData
    },
    showImg: (state, { payload }: PayloadAction<{ id: string }>) => {
      state.imaging.img.show = true
      state.imaging.img.id = payload.id
    },
    showLabResultDetails: (state, { payload }: PayloadAction<{ id: string; defaultData: LabResult }>) => {
      state.labResultDetails.show = true
      state.labResultDetails.id = payload.id
      state.labResultDetails.defaultData = payload.defaultData
    },
    showLocationSuccess: state => {
      state.location.success = true
    },
    showMedicalHistoryDetails: (
      state,
      { payload }: PayloadAction<{ id: string; defaultData: MedDataMock | undefined }>
    ) => {
      state.history.medical.details.show = true
      state.history.medical.details.id = payload.id
      state.history.medical.details.defaultData = payload.defaultData
    },
    showMedicationDetails: (
      state,
      { payload }: PayloadAction<{ id: string; defaultData: MedicationDataMock | undefined }>
    ) => {
      state.medication.detail.show = true
      state.medication.detail.id = payload.id
      state.medication.detail.defaultData = payload.defaultData
    },
    showMedicationFrequency: state => {
      state.medication.custom_frequency.add = true
    },
    showPatientDetail: (state, { payload }: PayloadAction<{ id: string }>) => {
      state.patient.detail.show = true
      state.patient.detail.id = payload.id
    },
    showPreviewImagingResult: (state, { payload }: PayloadAction<{ id: string; defaultData?: OrderDataMock }>) => {
      state.previewImagingResult = {
        defaultData: payload.defaultData,
        id: payload.id,
        show: true
      }
    },
    showProcedureDetails: (state, { payload }: PayloadAction<{ id: string; defaultData: ProceedAttributesMock }>) => {
      state.procedure.admin.detail.show = true
      state.procedure.admin.detail.id = payload.id
      state.procedure.admin.detail.defaultData = payload.defaultData
    },
    showProcedureOrderDetails: (
      state,
      { payload }: PayloadAction<{ id: string; defaultData: ConsentFormDataMock }>
    ) => {
      state.procedure.doctor.detail.show = true
      state.procedure.doctor.detail.id = payload.id
      state.procedure.doctor.detail.defaultData = payload.defaultData
    },
    showRoleSuccess: state => {
      state.permission.success = true
    },
    showScheduleDetails: (state, { payload }: PayloadAction<{ defaultData: SchedulEventMock[] }>) => {
      state.schedule.detail.show = true
      state.schedule.detail.defaultData = payload.defaultData
    },
    showSearch: state => {
      state.staff.search = true
    },
    showShiftSuccess: state => {
      state.shift.success = true
    },
    showStaffDetail: (state, { payload }: PayloadAction<{ id: string }>) => {
      state.staff.detail.show = true
      state.staff.detail.id = payload.id
    },
    showSubmitLabResult: (state, { payload }: PayloadAction<{ id: string }>) => {
      state.submitLabResult.show = true
      state.submitLabResult.id = payload.id
    },
    showSubmitLabSample: (state, { payload }: PayloadAction<{ id: string }>) => {
      state.submitLabSample.show = true
      state.submitLabSample.id = payload.id
    },
    showSupplierDetail: (state, { payload }: PayloadAction<{ id: string }>) => {
      state.supplier.detail.show = true
      state.supplier.detail.id = payload.id
    },
    showSupplierSuccess: state => {
      state.supplier.success = true
    },
    showUpdatePatient: (
      state,
      { payload }: PayloadAction<{ id: string; defaultData: (PatientSAttrProps & { email?: string }) | undefined }>
    ) => {
      state.patient.edit.show = true
      state.patient.edit.id = payload.id
      state.patient.edit.defaultData = payload.defaultData
    },
    showViewImagingResultFile: (
      state,
      {
        payload
      }: PayloadAction<{
        id: string
        defaultData: { url: string; filename: string; content_type: string; byte_size: number }
      }>
    ) => {
      state.viewImagingResultFile.show = true
      state.viewImagingResultFile.id = payload.id
      state.viewImagingResultFile.defaultData = payload.defaultData
    },
    showVitalDetails: (state, { payload }: PayloadAction<{ id: string; defaultData: VitalDataMock | undefined }>) => {
      state.vitals.detail.show = true
      state.vitals.detail.id = payload.id
      state.vitals.detail.defaultData = payload.defaultData
    },
    showVitalsGraph: state => {
      state.vitals.showGraph = true
    }
  }
})

export const {
  hideAddRoles,
  hideDeleteStaff,
  hideEditPermission,
  hideFilterStaff,
  hideSearch,
  showAddRoles,
  showDeleteStaff,
  showEditPermission,
  showFilterStaff,
  showSearch,
  showStaffDetail,
  hideAddPatient,
  hideDeletePatient,
  hideUpdatePatient,
  showAddPatient,
  showPatientDetail,
  showDeletePatient,
  showUpdatePatient,
  hideStaffDetail,
  hidePatientDetail,
  showDeleteRoles,
  hideDeleteRoles,
  hideRoleSuccess,
  showRoleSuccess,
  hideAddDepartment,
  showAddDepartment,
  showEditDepartment,
  hideEditDepartment,
  hideDeleteDepartment,
  showDeleteDepartment,
  hideDepSuccess,
  showDepSuccess,
  hideAddSupplier,
  showAddSupplier,
  hideDeleteSupplier,
  hideEditSupplier,
  hideSupplierDetail,
  hideSupplierSuccess,
  showDeleteSupplier,
  showEditSupplier,
  showSupplierDetail,
  showSupplierSuccess,
  hideAddLocation,
  hideDeleteLocation,
  hideEditLocation,
  hideLocationSuccess,
  showAddLocation,
  showDeleteLocation,
  showEditLocation,
  showLocationSuccess,
  hideAddShift,
  hideDeleteShift,
  hideEditShift,
  hideShiftSuccess,
  showAddShift,
  showDeleteShift,
  showEditShift,
  showShiftSuccess,
  hideAddSchedule,
  showAddSchedule,
  hideScheduleDetails,
  showScheduleDetails,
  hideEditSchedule,
  showEditSchedule,
  hideDeleteSchedule,
  showDeleteSchedule,
  hideAddEquipment,
  showAddEquipment,
  hideEquipmentDetails,
  showEquipmentDetails,
  showContact,
  hideContact,
  showAddMedicalHistory,
  hideAddMedicalHistory,
  showAddPersonal,
  hideAddPersonal,
  hideAddEmployment,
  showAddEmployment,
  hideAddGuardian,
  showAddGuardian,
  showEditVitals,
  hideEditVitals,
  showAddCompareVitals,
  hideAddCompareVitals,
  showCompareVitalsTable,
  hideCompareVitalsTable,
  showAddMisc,
  hideAddMisc,
  showDeleteVitals,
  hideDeleteVitals,
  showVitalDetails,
  hideVitalDetails,
  showEditMedicalHistory,
  hideEditMedicalHistory,
  showMedicalHistoryDetails,
  hideMedicalHistoryDetails,
  showDeleteMedicalHistory,
  hideDeleteMedicalHistory,
  showDoctorAppointment,
  hideDoctorAppointment,
  hideAddMedications,
  showAddMedications,
  hideMedicationDetails,
  showMedicationDetails,
  showEditMedications,
  hideEditMedications,
  hideDeleteMedications,
  showDeleteMedications,
  showMedicationFrequency,
  hideMedicationFrequency,
  showEditMedicationFrequency,
  hideEditMedicationFrequency,
  hideVitalsGraph,
  showVitalsGraph,
  showAddProblems,
  hideAddProblems,
  showEditProblems,
  hideEditProblems,
  showAddProcedure,
  hideAddProcedure,
  showEditProcedure,
  hideEditProcedure,
  showDeleteProcedure,
  hideDeleteProcedure,
  showProcedureDetails,
  hideProcedureDetails,
  showEditProcedureOrder,
  hideEditProcedureOrder,
  showProcedureOrderDetails,
  hideProcedureOrderDetails,
  showAddProcedureNotes,
  hideAddProcedureNotes,
  showCancelProcedure,
  hideCancelProcedure,
  showEditLab,
  hideEditLab,
  showDeleteLab,
  hideDeleteLab,
  showAddImaging,
  hideAddImaging,
  showEditImaging,
  hideEditImaging,
  showDeleteImaging,
  hideDeleteImaging,
  showImagingDetails,
  hideImagingDetails,
  showImg,
  hideImg,
  showAddAdmissionOrder,
  hideAddAdmissionOrder,
  showEditAdmissionOrder,
  hideEditAdmissionOrder,
  showDeleteAdmissionOrder,
  hideDeleteAdmissionOrder,
  showSubmitLabSample,
  hideSubmitLabSample,
  showSubmitLabResult,
  hideSubmitLabResult,
  showPreviewImagingResult,
  hidePreviewImagingResult,
  showLabResultDetails,
  hideLabResultDetails,
  showImagingResultDetails,
  hideImagingResultDetails,
  showViewImagingResultFile,
  hideViewImagingResultFile
} = ModalSlicer.actions

export default ModalSlicer.reducer
